import * as React from "react"
import { cn } from "@/lib/utils"

/**
 * Compact, visually consistent metric card for dashboards.
 *
 * <StatCard label="Realization" value="87%" hint="billable → invoiced" />
 *
 * Props intentionally kept small — the dashboard shouldn't be a
 * customization playground.
 */
export interface StatCardProps extends React.HTMLAttributes<HTMLDivElement> {
  label: React.ReactNode
  value: React.ReactNode
  hint?: React.ReactNode
  trend?: "up" | "down" | "flat"
  trendLabel?: React.ReactNode
  icon?: React.ReactNode
}

export function StatCard({
  label,
  value,
  hint,
  trend,
  trendLabel,
  icon,
  className,
  ...props
}: StatCardProps) {
  return (
    <div
      className={cn(
        "rounded-lg border bg-card p-4 shadow-sm",
        className,
      )}
      {...props}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {label}
          </div>
          <div className="mt-1 text-2xl font-semibold leading-none tracking-tight">
            {value}
          </div>
          {hint ? (
            <div className="mt-1 text-xs text-muted-foreground">{hint}</div>
          ) : null}
        </div>
        {icon ? <div className="shrink-0 text-muted-foreground">{icon}</div> : null}
      </div>
      {trend && trendLabel ? (
        <div
          className={cn(
            "mt-3 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs",
            trend === "up" && "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
            trend === "down" && "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300",
            trend === "flat" && "bg-muted text-muted-foreground",
          )}
        >
          <span aria-hidden>
            {trend === "up" ? "▲" : trend === "down" ? "▼" : "—"}
          </span>
          {trendLabel}
        </div>
      ) : null}
    </div>
  )
}
